import {useEffect, useState} from "react";
import {authService} from "../../service/auth/auth.service";

const getEmailToCheck = (lookupData, cosmosCustomer, lookUpResponse) => {
    if (lookupData.fieldType === "EMAIL") {
        return lookupData.searchValue;
    }
    return cosmosCustomer?.email || lookUpResponse?.meta?.wynshopCustomer?.email || "";
};

const fetchSuppressionStatus = (email) =>
    authService.getKlaviyoSuppressionStatus(email).then(status => {
        if (!status || status.error) {
            return null;
        }
        return status;
    });

export const useSuppressionStatus = ({lookUpResponse, lookupData, cosmosCustomer, showAlert, onSuppressed}) => {
    const [suppressionStatus, setSuppressionStatus] = useState(null);
    const [isUnsuppressing, setIsUnsuppressing] = useState(false);

    useEffect(() => {
        const emailToCheck = lookUpResponse?.data
            ? getEmailToCheck(lookupData, cosmosCustomer, lookUpResponse)
            : "";

        if (!emailToCheck) {
            setSuppressionStatus(null);
            return;
        }

        fetchSuppressionStatus(emailToCheck).then(status => {
            setSuppressionStatus(status);

            if (status?.suppressed) {
                const reasons = status.reasons ?? [];
                showAlert(`Email marketing is suppressed for ${emailToCheck}: ${reasons.join("; ")}`, "danger");
                onSuppressed(reasons);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lookUpResponse]);

    // Re-check Klaviyo so the banner only clears once Klaviyo confirms the suppression is resolved.
    const confirmUnsuppressed = (email) =>
        fetchSuppressionStatus(email).then(status => {
            if (status) {
                setSuppressionStatus(status);
            }
            if (status && !status.suppressed) {
                showAlert(`Unsuppressed ${email} successfully!`, "success");
            } else {
                showAlert(`Unsuppress request sent for ${email}, but Klaviyo still reports it as suppressed.`, "warning");
            }
        });

    const handleUnsuppress = () => {
        const email = suppressionStatus?.email;
        if (!email) {
            return;
        }

        setIsUnsuppressing(true);

        authService.unsuppressKlaviyoProfile(email).then(data => {
            setIsUnsuppressing(false);
            if (data && !data.error) {
                confirmUnsuppressed(email);
            } else {
                showAlert(data?.error || "Failed to unsuppress profile.", "danger");
            }
        }, error => {
            setIsUnsuppressing(false);
            showAlert(error?.message || "Failed to unsuppress profile.", "danger");
        });
    };

    return {suppressionStatus, isUnsuppressing, handleUnsuppress};
};

export const useBlockStatus = ({lookUpResponse, customerId, showAlert}) => {
    const [blockStatus, setBlockStatus] = useState(null);
    const [isUnblocking, setIsUnblocking] = useState(false);

    useEffect(() => {
        if (!lookUpResponse?.data || !customerId) {
            setBlockStatus(null);
            return;
        }

        authService.isAccountBlocked(customerId).then(status => {
            if (!status?.success) {
                setBlockStatus(null);
                return;
            }

            setBlockStatus(status);

            if (status.isBlocked) {
                showAlert(status.message || "This account is blocked.", "danger");
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lookUpResponse, customerId]);

    const handleUnblockAccount = () => {
        if (!customerId) {
            return;
        }

        setIsUnblocking(true);

        authService.unblockAccount(customerId).then(data => {
            setIsUnblocking(false);
            if (data?.success) {
                showAlert(data.message || "Unblocked account successfully!", "success");
                setBlockStatus(prev => prev && {...prev, isBlocked: false});
            } else {
                showAlert(data?.message || "Failed to unblock account.", "danger");
            }
        }, error => {
            setIsUnblocking(false);
            showAlert(error?.message || "Failed to unblock account.", "danger");
        });
    };

    return {blockStatus, isUnblocking, handleUnblockAccount};
};

export const useDefaultStore = ({storeNumber, stores, autoSet, changeStoreLocation}) => {
    const [defaultStore, setDefaultStore] = useState();

    useEffect(() => {
        const foundStore = storeNumber
            ? stores.find(store => store.storeNumber?.toString() === storeNumber.toString())
            : undefined;

        setDefaultStore(foundStore);

        if (foundStore && !autoSet) {
            changeStoreLocation(
                {target: {name: "storeLocation", value: foundStore.originalEcomName}},
                foundStore.storeNumber
            );
        }
    }, [storeNumber, stores, autoSet, changeStoreLocation]);

    return defaultStore;
};

export const useAccountActions = ({user, lookUpResponse, showAlert, setLookUpResponse}) => {
    const confirmAndRun = ({confirmMessage, request, isSuccess, successMessage, getErrorMessage}) => (e) => {
        e.preventDefault();
        if (!window.confirm(confirmMessage)) {
            return;
        }

        request().then(data => {
            if (isSuccess(data)) {
                showAlert(successMessage, "success");
                setLookUpResponse({});
            } else {
                showAlert(getErrorMessage(data), "danger");
            }
        }, error => {
            showAlert(error, "danger");
        });
    };

    const handleDeleteAccount = confirmAndRun({
        confirmMessage: "Are you sure you want to proceed?",
        request: () => authService.deleteCustomerRecord(user?.email),
        isSuccess: data => Boolean(data?.message) && data.statusCode !== "500",
        successMessage: `Deleted ${user?.email} successfully!`,
        getErrorMessage: data => data?.message
    });

    const handleResetPasswordLink = confirmAndRun({
        confirmMessage: "Do you want to send password link?",
        request: () => authService.resetPassword(user?.customerId),
        isSuccess: data => Boolean(data?.success),
        successMessage: `Email sent to ${user?.email} successfully!`,
        getErrorMessage: data => data?.error
    });

    const handleVerifyEmailLink = confirmAndRun({
        confirmMessage: "Do you want to send verify email link?",
        request: () => authService.sendEmailVerifyLink(user?.customerId),
        isSuccess: data => Boolean(data) && !data.error,
        successMessage: `Email sent to ${user?.email} successfully!`,
        getErrorMessage: data => data?.error
    });

    const handleUpdateWynshopLoyaltyId = (customerId) => {
        authService.updateWynshopLoyaltyId(customerId).then(data => {
            if (data && !data.error) {
                // Mutated in place (not via setLookUpResponse) so the lookup effects don't re-run and re-alert.
                const wynshopCustomer = lookUpResponse?.meta?.wynshopCustomer;
                if (wynshopCustomer?.loyaltyId) {
                    wynshopCustomer.loyaltyId = wynshopCustomer.loyaltyId.slice(0, 11);
                }
                showAlert("Updated loyaltyId successful!", "success");
            } else {
                showAlert(data?.error, "danger");
            }
        }, error => {
            showAlert(error, "danger");
        });
    };

    return {handleDeleteAccount, handleResetPasswordLink, handleVerifyEmailLink, handleUpdateWynshopLoyaltyId};
};
